import { useEffect, useMemo, useState } from "react";
import { Trash2, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  unlinkAvailabilityCalendar,
  updateLinkedAvailabilityCalendar,
} from "../../../lib/data/availability";
import { DEFAULT_COLORS } from "./LinkCalendarDialog";

const VOTE_LEVELS = [
  { value: "PREFERRED", label: "Preferred" },
  { value: "FEASIBLE", label: "Feasible" },
];

/**
 * Edit dialog for an already-linked availability calendar.
 *
 * Surface:
 *   - Read-only: name, type
 *   - Editable: color, defaultVoteLevel (free-type only),
 *     trustEnabled (conflict-type only)
 *   - Destructive: Unlink (top-right, with inline confirm)
 *
 * Phase 2 will add type-change + leeway. The trustEnabled toggle (Phase 2
 * Chunk C) is the only auto-vote-related field on this dialog so far.
 */
export default function EditLinkedCalendarDialog({
  open,
  onOpenChange,
  calendar,
  onMutated,
}) {
  const [color, setColor] = useState(calendar?.color || DEFAULT_COLORS[0]);
  const [voteLevel, setVoteLevel] = useState(
    calendar?.defaultVoteLevel || "PREFERRED"
  );
  const [trustEnabled, setTrustEnabled] = useState(
    calendar?.trustEnabled !== false
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmingUnlink, setConfirmingUnlink] = useState(false);

  // Reset local state whenever the dialog opens with a (possibly different) calendar.
  useEffect(() => {
    if (open && calendar) {
      setColor(calendar.color || DEFAULT_COLORS[0]);
      setVoteLevel(calendar.defaultVoteLevel || "PREFERRED");
      setTrustEnabled(calendar.trustEnabled !== false);
      setConfirmingUnlink(false);
      setError(null);
    }
  }, [open, calendar]);

  const isFree = calendar?.calendarType === "free";

  const dirty = useMemo(() => {
    if (!calendar) return false;
    if (color !== (calendar.color || "")) return true;
    if (isFree && voteLevel !== (calendar.defaultVoteLevel || "PREFERRED")) return true;
    if (!isFree && trustEnabled !== (calendar.trustEnabled !== false)) return true;
    return false;
  }, [calendar, color, voteLevel, isFree, trustEnabled]);

  if (!calendar) return null;

  async function handleSave() {
    setBusy(true);
    setError(null);
    try {
      const payload = { calendarId: calendar.id };
      if (color !== calendar.color) payload.color = color;
      if (isFree && voteLevel !== calendar.defaultVoteLevel) {
        payload.defaultVoteLevel = voteLevel;
      }
      if (!isFree && trustEnabled !== (calendar.trustEnabled !== false)) {
        payload.trustEnabled = trustEnabled;
      }
      await updateLinkedAvailabilityCalendar(payload);
      if (onMutated) onMutated();
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || "Failed to save changes.");
    } finally {
      setBusy(false);
    }
  }

  async function handleUnlink() {
    setBusy(true);
    setError(null);
    try {
      await unlinkAvailabilityCalendar({ calendarId: calendar.id });
      if (onMutated) onMutated();
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || "Failed to unlink calendar.");
      setConfirmingUnlink(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle>Edit linked calendar</DialogTitle>
              <DialogDescription>
                Update how this calendar is shown on your availability page.
              </DialogDescription>
            </div>
            {!confirmingUnlink ? (
              <button
                type="button"
                onClick={() => setConfirmingUnlink(true)}
                disabled={busy}
                className="flex shrink-0 items-center gap-1.5 rounded-xl border border-rose-200 px-2.5 py-1.5 text-xs font-medium text-rose-700 transition-colors hover:bg-rose-50 disabled:opacity-50 dark:border-rose-900/50 dark:text-rose-300 dark:hover:bg-rose-900/30"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                Unlink
              </button>
            ) : (
              <div className="flex shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleUnlink}
                  disabled={busy}
                  className="rounded-xl bg-rose-600 px-2.5 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-rose-700 disabled:opacity-50"
                >
                  Confirm unlink
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingUnlink(false)}
                  disabled={busy}
                  className="rounded-xl border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  aria-label="Cancel unlink"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </DialogHeader>

        {error && (
          <p className="rounded-xl bg-rose-100 px-3 py-2 text-sm text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
            {error}
          </p>
        )}

        <div className="space-y-4 py-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Calendar
            </p>
            <p className="mt-1 text-sm text-slate-900 dark:text-slate-100">
              {calendar.googleCalendarSummary || calendar.id}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Type
            </p>
            <p className="mt-1 text-sm text-slate-900 dark:text-slate-100">
              {isFree ? "Free time" : "Conflict"}
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Changing type comes in a future update — unlink and re-link to
              switch for now.
            </p>
          </div>

          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Color
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {DEFAULT_COLORS.map((c) => {
                const selected = color === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    disabled={busy}
                    aria-label={`Color ${c}${selected ? " (selected)" : ""}`}
                    aria-pressed={selected}
                    className="relative h-8 w-8 rounded-full ring-2 ring-offset-2 ring-offset-white transition-shadow disabled:opacity-50 dark:ring-offset-slate-900"
                    style={{
                      backgroundColor: c,
                      "--tw-ring-color": selected ? c : "transparent",
                    }}
                  >
                    {selected && (
                      <Check
                        className="absolute inset-0 m-auto h-4 w-4 text-white"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {!isFree && (
            <div>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  aria-label="Trust this calendar's events for auto-NO"
                  checked={trustEnabled}
                  onChange={(e) => setTrustEnabled(e.target.checked)}
                  disabled={busy}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 disabled:opacity-50"
                />
                <span>
                  <strong className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    Trust this calendar&apos;s events for auto-NO
                  </strong>
                  <br />
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    Uncheck to render events on the calendar but skip auto-NO on conflicting slots.
                  </span>
                </span>
              </label>
            </div>
          )}

          {isFree && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Default vote level
              </p>
              <div className="mt-2 flex gap-2">
                {VOTE_LEVELS.map((lvl) => {
                  const selected = voteLevel === lvl.value;
                  return (
                    <button
                      key={lvl.value}
                      type="button"
                      onClick={() => setVoteLevel(lvl.value)}
                      disabled={busy}
                      className={`flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
                        selected
                          ? "border-brand-primary bg-brand-primary/10 text-brand-primary dark:border-brand-primary dark:bg-brand-primary/20 dark:text-blue-300"
                          : "border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                      }`}
                    >
                      {lvl.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy || !dirty}
            className="rounded-xl bg-brand-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-primary/90 disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
